import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import Input from '../../../components/Input';
import GeneralModal from '../../../components/GeneralModal/GeneralModal';
import { useAddLocale, useAvailableLocales, useUpdateLocale } from '../../../hooks/useLanguages';
import { useTranslation } from '../../../hooks/useTranslation';
import {toast} from 'react-toastify';

const LanguageModal = ({ 
  isOpen, 
  onClose, 
  editingLanguage = null,
  setEditingLanguage,
  refetch
}) => {
  const { t } = useTranslation();
    
    // Status options for the dropdown
    const statusOptions = [
      { value: true, label: t('languageModal.statusOptions.active') },
      { value: false, label: t('languageModal.statusOptions.inactive') }
    ];
    const { data: availableLocales } = useAvailableLocales({enabled: isOpen});
    const { mutate: updateLocale } = useUpdateLocale({
      onSuccess: (data) => {
        if (data) {
          toast.success(t('languageModal.messages.languageUpdatedSuccess'));
          handleCloseModal();
        }
      },
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    });
  const { mutate: addLocale } = useAddLocale({
      onSuccess: (data) => {
          if (data) {
              toast.success(t('languageModal.messages.languageAddedSuccess'));
              handleCloseModal();
          }
    },
    onError: (error) => {
        toast.error(error.response.data.message);
    },
  });
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    local_name: '',
    is_active: true
  });

    const [errors, setErrors] = useState({});
    
    // Create locale options from available locales
  const localeOptions = availableLocales?.map(locale => ({
    value: locale,
    label: locale.toUpperCase()
  })) || [];

  // Populate form when editing a language
  useEffect(() => {
    if (editingLanguage && isOpen && availableLocales) {
      setFormData({
        code: editingLanguage.code || '',
        name: editingLanguage.name || '',
        local_name: editingLanguage.local_name || '',
        is_active: Boolean(editingLanguage.is_active)
      });
    } else if (!editingLanguage && isOpen) {
      setFormData({
        code: '',
        name: '',
        local_name: '',
        is_active: true
      });
    }
    setErrors({});
  }, [editingLanguage, isOpen, availableLocales]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      is_active: selectedOption ? selectedOption.value : true
    }));
    
    // Clear error when user makes selection
    if (errors.is_active) {
      setErrors(prev => ({
        ...prev,
        is_active: ''
      }));
    }
  };

  const handleLanguageCodeChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      code: selectedOption ? selectedOption.value : ''
    }));
    
    // Clear error when user makes selection
    if (errors.code) {
      setErrors(prev => ({
        ...prev,
        code: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Only validate code when adding a new language (not when editing)
    if (!editingLanguage) {
      if (!formData.code || !formData.code.trim()) {
        newErrors.code = t('languageModal.errors.codeRequired');
      } else if (formData.code.length < 2) {
        newErrors.code = t('languageModal.errors.codeMinLength');
      }
    }

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = t('languageModal.errors.nameRequired');
    }

    // local_name is optional, no validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      handleSubmitLanguage(formData);
    }
  };
    
    // Modal handlers

  const handleCloseModal = () => {
    onClose();
    setEditingLanguage(null);
    setFormData({
      code: '',
      name: '',
      local_name: '',
      is_active: true
    });
    setErrors({});
  };

    const handleSubmitLanguage = async (formData) => {
      if (editingLanguage) {
        // Update existing language
        updateLocale({
          id: editingLanguage.id,
          ...formData
        });
      } else {
        // Add new language
        addLocale(formData);
      }
  };


    const modalTitle = editingLanguage ? t('languageModal.title.edit') : t('languageModal.title.add');
    const modalSubmitTitle = editingLanguage ? t('languageModal.submitTitle.update') : t('languageModal.submitTitle.add');

  if (!isOpen) return null;

  return (
    <GeneralModal
      title={modalTitle}
      onClose={handleCloseModal}
      onSubmit={handleSubmit}
      submitTitle={modalSubmitTitle}
      cancelTitle={t('languageModal.cancelTitle')}
      showCancelBtn={true}
      showSubmitBtn={true}
      disableSubmit={false}
    >
      <div className="language-management__modal-form">
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '500',
            marginBottom: '8px',
            color: '#333'
          }}>
            {t('languageModal.languageCode')}
          </label>
          <Select
            name="code"
            options={localeOptions}
            value={localeOptions.find(option => option.value === formData.code) || null}
            onChange={handleLanguageCodeChange}
            isDisabled={!!editingLanguage}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                minHeight: 42,
                backgroundColor: editingLanguage ? '#e9ecef' : '#F4F5F6',
              }),
            }}
            placeholder={t('customSelect.select')}
          />
          {errors.code && (
            <div className="error-message">
              {errors.code}
            </div>
          )}
        </div>

        <div style={{ marginTop: '16px' }}>
          <Input
            label={t('languageModal.languageName')}
            name="name"
            placeholder={t('languageModal.languageNamePlaceholder')}
            value={formData.name}
            onChange={handleInputChange}
            required
            containerWidth="100%"
          />
          {errors.name && (
            <div className="error-message">
              {errors.name}
            </div>
          )}
        </div>

        <div style={{ marginTop: '16px' }}>
          <Input
            label={t('languageModal.localName')}
            name="local_name"
            placeholder={t('languageModal.localNamePlaceholder')}
            value={formData.local_name}
            onChange={handleInputChange}
            containerWidth="100%"
          />
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '500',
            marginBottom: '8px',
            color: '#333'
          }}>
            {t('languageModal.status')}
          </label>
          <Select
            name="is_active"
            options={statusOptions}
            value={statusOptions.find(option => option.value === formData.is_active) || null}
            onChange={handleSelectChange}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                minHeight: 42,
                backgroundColor: '#F4F5F6',
              }),
            }}
            placeholder={t('customSelect.select')}
          />
          {errors.is_active && (
            <div className="error-message">
              {errors.is_active}
            </div>
          )}
        </div>
      </div>
    </GeneralModal>
  );
};

export default LanguageModal;
